import React, {useState, useEffect} from "react";
import {useHistory, Link} from "react-router-dom";
import {useObserver, observer} from "mobx-react-lite";
import {Menu, Button} from "antd";
import {LogoutOutlined, MenuOutlined, SettingOutlined, UserOutlined} from "@ant-design/icons";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {VIGO_PANEL_LOGO, V_LOGO} from "../../constants/constants";
import {useStore} from "../../hooks/useStore";
import {
    routes,
    customerRoutes,
    poolCustomerRoutes,
    poolAndUatCustomerRoutes,
    dedicatedCustomerRoutes
} from "../../routes/routes";
import {Loading} from "../_defaultComponents/Loading/Loading";

import {
    LayoutContainer,
    LayoutSider,
    RightLayout,
    LayoutHeader,
    LayoutFooter,
    ChildrenContainer,
    LayoutContent,
    LogoImg,
    CompanyImg,
    MenuText,
    BottomMenu,
    LogoContainer,
    MobileMenuToggle,
    MenuPopupGlobalStyle
} from "./layoutStyles";
// Supsis kullanımı durdurulmuştur
// import {SupsisConfirmationModal} from "../_defaultComponents/SupsisConfirmationModal/SupsisConfirmationModal";
// import {SupsisSubcompanyChangeModal} from "../_defaultComponents/SupsisSubcompanyChangeModal/SupsisSubcompanyChangeModal";
import {MenuSearch} from "../_defaultComponents/MenuSearch/MenuSearch";
import CompanyPlaceholder from "../../public/images/company-placeholder.jpg";
import config from "../../_core/config";
import VigoMobileLogo from "./VigoMobileLogo";
import {getStartRoute} from "../../utils/getStartRoute";
import {CustomerTheme} from "./customerTheme/CustomerTheme";
import {LoginLayout} from "../layouts/loginLayout/LoginLayout";
import {setSentryUser} from "../../index";

const {SubMenu} = Menu;

// Ek ikon/rozet olan menü öğelerinde satırı flex yapıp taşmayı metne yıkıyoruz,
// böylece logo ve rozet "..." ile kesilmiyor
const menuExtrasRowStyle = {display: "flex", alignItems: "center", minWidth: 0};
const menuExtrasTextStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0
};

// Menü metninin sağında gösterilen ek ikon (ör. Metabase logosu)
const MenuSuffixIcon = ({icon}) => (
    <span
        style={{
            display: "inline-block",
            marginLeft: "6px",
            // antd, menü öğesi içindeki her .anticon'a 10px sağ boşluk veriyor
            marginRight: "-10px",
            verticalAlign: "middle",
            flexShrink: 0,
            lineHeight: 1
        }}
    >
        {icon}
    </span>
);

// Menü metninin sağında gösterilen "YENİ" gibi küçük etiketler
const MenuBadge = ({label}) => (
    <span
        style={{
            marginLeft: "8px",
            padding: "0 5px",
            fontSize: "9px",
            fontWeight: "bold",
            lineHeight: "15px",
            color: "#ffffff",
            backgroundColor: "#ed4532",
            borderRadius: "8px",
            letterSpacing: "0.5px",
            flexShrink: 0
        }}
    >
        {label}
    </span>
);

const mapPaths = ["/map", "/heatmap", "/create-delivery-request", "/orders"];
const isMap = pathname => {
    if (mapPaths.includes(pathname)) {
        return true;
    }
    if (/^\/orders\/[a-f0-9]+$/i.test(pathname)) {
        return true;
    }
    return false;
};
// Alt kullanıcıya (Acente Temsilcisi) yalnızca yetkisi verilen sayfalar gösterilir; hiç alt menüsü
// kalmayan başlıklar düşer.
const filterRoutesByPermission = (allRoutes, canAccessPath) =>
    allRoutes
        .map(route => {
            if (!route.subMenus) {
                return canAccessPath(route.path) ? route : null;
            }
            const subMenus = route.subMenus.filter(subRoute =>
                canAccessPath(subRoute.path || `${route.path || ""}${subRoute.subPath || ""}`)
            );
            return subMenus.length > 0 ? {...route, subMenus} : null;
        })
        .filter(Boolean);

// Rol listesinde olmayan kullanıcıya, menü öğesine bağlı dinamik yetki politikası varsa öğe
// rolüne açılır; böylece mevcut `roles.includes(role)` kontrolleri değişmeden çalışır.
const grantRoutesByPolicy = (allRoutes, role, can) => {
    const grant = item =>
        item.permissionKeys?.some(key => can(key)) && !item.roles.includes(role)
            ? {...item, roles: [...item.roles, role]}
            : item;
    return allRoutes.map(route => {
        const granted = grant(route);
        return granted.subMenus ? {...granted, subMenus: granted.subMenus.map(grant)} : granted;
    });
};

export const Theme = observer(props => {
    const stores = useStore();
    const history = useHistory();
    const {i18n} = useTranslation();
    const [pathname, setPathname] = useState(
        getStartRoute({
            role: stores?.meStore?.role,
            isPoolCustomer: stores?.meStore?.isPoolCustomer,
            isUatCustomer: stores?.meStore?.isUatCustomer
        })
    );
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [collapsed, setCollapsed] = useState(window.innerWidth <= 768);
    // Supsis kullanımı durdurulmuştur
    // const [isSupsisActive, setIsSupsisActive] = useState(false);
    // const [supsisActiveSubcompanyId, setSupsisActiveSubcompanyId] = useState(null);
    // const [isOpeningSupsis, setIsOpeningSupsis] = useState(false);
    const [menuSearchValue, setMenuSearchValue] = useState("");
    const [menuOpenKeys, setMenuOpenKeys] = useState([]);

    // Supsis kullanımı durdurulmuştur
    // const [supsisConfirmationModalConfig, setSupsisConfirmationModalConfig] = useState({
    //     visible: false,
    //     subcompanyName: "Seçili alt firma",
    //     onConfirm: null,
    //     onCancel: null
    // });
    // const [subcompanyChangeModalConfig, setSubcompanyChangeModalConfig] = useState({
    //     visible: false,
    //     currentName: "Mevcut alt firma",
    //     newName: "Yeni alt firma",
    //     okText: "Tamam",
    //     cancelText: "İptal",
    //     onConfirm: null,
    //     onCancel: null
    // });

    const {isLoggedIn, role, company, companies} = stores.meStore;
    const image =
        company && company.photo
            ? `${config.getBasePublicUrl()}api/panel/v1/image/${company.photo}`
            : CompanyPlaceholder;

    useEffect(() => {
        history.listen(location => {
            stores.appStore.setCurrentPath(location.pathname);
        });
    });

    useEffect(() => {
        stores.appStore.setCurrentPath(history.location.pathname);
        if (isLoggedIn) {
            stores.appStore.getCompanyUserList();
            stores.appStore.getCompanyListFilter();
            setSentryUser(stores.meStore);
        } else {
            setSentryUser(null);
        }
    }, []);

    useEffect(() => {
        setPathname(history.location.pathname);
    }, [history.location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) {
                setCollapsed(true);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (collapsed) {
            setMenuSearchValue("");
            setMenuOpenKeys([]);
        }
    }, [collapsed]);

    useEffect(() => {
        if (menuSearchValue.trim()) {
            setMenuOpenKeys(getOpenKeysForSearch());
        } else {
            setMenuOpenKeys([]);
        }
    }, [menuSearchValue]);

    const toLogout = async () => {
        await stores.appStore.setLoading(true);
        await stores.meStore.logout();
        await history.push("/login");
        await stores.appStore.setLoading(false);
    };

    const toAppConfig = async () => {
        await stores.appStore.setLoading(true);
        await history.push("/appconfig");
        await stores.appStore.setLoading(false);
    };

    const extra = [
        <React.Fragment key={1}>
            {stores.meStore.checkRole("appConfig") && (
                <Button
                    key="appConfig"
                    data-testid="appConfigButton"
                    onClick={() => toAppConfig()}
                    icon={<SettingOutlined />}
                />
            )}
            <Button
                onClick={() => i18n.changeLanguage(i18n.resolvedLanguage === "en" ? "tr" : "en")}
            >
                {i18n.resolvedLanguage === "en" ? "EN" : "TR"}
            </Button>

            <Button
                key="logout"
                data-testid="logoutButton"
                onClick={() => toLogout()}
                icon={<LogoutOutlined />}
            />
        </React.Fragment>
    ];

    const toProfile = async () => {
        await stores.appStore.setLoading(true);
        await history.push("/profile");
        await stores.appStore.setLoading(false);
    };

    extra.splice(
        0,
        0,
        company ? (
            <CompanyImg onClick={() => toProfile()} src={image} />
        ) : (
            <Button
                data-testid="profileButton"
                key="profile"
                onClick={() => toProfile()}
                icon={<UserOutlined />}
            />
        )
    );

    const isMenuActive = routePath => {
        if (window.location.pathname.indexOf(routePath) == -1) return false;

        if (window.location.pathname.replace("/panel", "").replace(routePath, "").length > 0)
            return false;

        return true;
    };

    const isCompanyUser = stores.meStore.checkRole("isCompanyUser");

    // Helper functions
    const getSubcompanyName = subcompanyId => {
        return (
            stores.meStore.subcompanies?.find(subcompany => subcompany._id === subcompanyId)
                ?.name || "Seçili alt firma"
        );
    };

    const getRoutes = () => {
        if (!stores.meStore.checkRole("isCompanyUser")) {
            const grantedRoutes = grantRoutesByPolicy(routes, role, stores.meStore.can);
            return stores.meStore.isSubAgency
                ? filterRoutesByPermission(grantedRoutes, stores.meStore.canAccessPath)
                : grantedRoutes;
        }
        if (stores.meStore.isDedicatedCustomer) return dedicatedCustomerRoutes;
        if (stores.meStore.checkRole("isPoolCustomer") && stores.meStore.checkRole("isUatCustomer"))
            return poolAndUatCustomerRoutes;
        if (stores.meStore.checkRole("isPoolCustomer")) return poolCustomerRoutes;
        return customerRoutes;
    };

    const matchesSearch = route => {
        if (!menuSearchValue.trim()) return true;
        try {
            const routeName = typeof route?.name === "function" ? route?.name() : route?.name || "";
            return routeName?.toLowerCase()?.includes(menuSearchValue?.toLowerCase()?.trim());
        } catch (e) {
            return true;
        }
    };

    // suffixIconRoles tanımlıysa işaret sadece o rollere gösterilir, yoksa herkese açıktır.
    const canSeeSuffixIcon = route =>
        !route?.suffixIconRoles || route.suffixIconRoles.includes(role);

    const hasMatchingSubMenu = route => {
        if (!menuSearchValue.trim()) return true;
        if (!route.subMenus || !Array.isArray(route.subMenus)) return false;
        return route.subMenus.some(subRoute => {
            try {
                const subRouteName =
                    typeof subRoute.name === "function" ? subRoute.name() : subRoute.name || "";
                return subRouteName.toLowerCase().includes(menuSearchValue.toLowerCase().trim());
            } catch (e) {
                return false;
            }
        });
    };

    const getMatchingSubRoute = route => {
        if (!menuSearchValue.trim() || !route.subMenus || !Array.isArray(route.subMenus))
            return null;
        return route.subMenus.find(subRoute => {
            try {
                const subRouteName =
                    typeof subRoute.name === "function" ? subRoute.name() : subRoute.name || "";
                return subRouteName.toLowerCase().includes(menuSearchValue.toLowerCase().trim());
            } catch (e) {
                return false;
            }
        });
    };

    const hasSearchResults = () => {
        if (!menuSearchValue.trim()) return true;
        return getRoutes().some(
            route =>
                route.navPosition === "top" &&
                route.roles.includes(role) &&
                (matchesSearch(route) || hasMatchingSubMenu(route))
        );
    };

    const getOpenKeysForSearch = () => {
        if (!menuSearchValue.trim()) return [];
        return getRoutes()
            .filter(
                route =>
                    route.navPosition === "top" &&
                    route.roles.includes(role) &&
                    route.subMenus &&
                    (matchesSearch(route) || hasMatchingSubMenu(route))
            )
            .map(route =>
                route.companyUserPage && route.path === "/companies"
                    ? "/companies/" + stores?.meStore?.company?._id
                    : route.path
            );
    };

    // Supsis kullanımı durdurulmuştur
    // const restartSupsisChat = () => {
    //     setIsSupsisActive(false);
    //     if (window.supsis) {
    //         try {
    //             window.supsis("closeChat");
    //         } catch (e) {
    //             console.error(e);
    //         }
    //     }
    //     setTimeout(() => {
    //         updateSupsisUserData();
    //         setTimeout(() => {
    //             initSupsis();
    //         }, 100);
    //     }, 50);
    // };

    // Supsis kullanımı durdurulmuştur
    // const canOpenModal = () => {
    //     return !subcompanyChangeModalConfig.visible && !supsisConfirmationModalConfig.visible;
    // };

    // Supsis kullanımı durdurulmuştur
    // const openSupsisConfirmationModal = ({subcompanyName, onConfirm, onCancel}) => {
    //     if (!canOpenModal()) return;
    //     window.__supsisModalOpen = true;
    //     setSupsisConfirmationModalConfig({
    //         visible: true,
    //         subcompanyName: subcompanyName || "Seçili alt firma",
    //         onConfirm,
    //         onCancel
    //     });
    // };

    // Supsis kullanımı durdurulmuştur
    // const closeSupsisConfirmationModal = ({resetModalOpenFlag = true} = {}) => {
    //     if (resetModalOpenFlag) {
    //         window.__supsisModalOpen = false;
    //     }
    //     setSupsisConfirmationModalConfig(prev => ({
    //         ...prev,
    //         visible: false,
    //         onConfirm: null,
    //         onCancel: null
    //     }));
    // };

    // Supsis kullanımı durdurulmuştur
    // const handleSupsisConfirmationOk = () => {
    //     const {onConfirm} = supsisConfirmationModalConfig;
    //     closeSupsisConfirmationModal({resetModalOpenFlag: false});
    //     if (typeof onConfirm === "function") {
    //         onConfirm();
    //     }
    // };

    // Supsis kullanımı durdurulmuştur
    // const handleSupsisConfirmationCancel = () => {
    //     const {onCancel} = supsisConfirmationModalConfig;
    //     closeSupsisConfirmationModal();
    //     setIsOpeningSupsis(false);
    //     if (typeof onCancel === "function") {
    //         onCancel();
    //     }
    // };

    // Supsis kullanımı durdurulmuştur
    // const openSubcompanyChangeModal = ({
    //     currentName,
    //     newName,
    //     okText = "Tamam",
    //     cancelText = "İptal",
    //     onConfirm,
    //     onCancel
    // }) => {
    //     if (!canOpenModal()) return;
    //     setSubcompanyChangeModalConfig({
    //         visible: true,
    //         currentName: currentName || "Mevcut alt firma",
    //         newName: newName || "Yeni alt firma",
    //         okText,
    //         cancelText,
    //         onConfirm,
    //         onCancel
    //     });
    // };

    // Supsis kullanımı durdurulmuştur
    // const closeSubcompanyChangeModal = () => {
    //     setSubcompanyChangeModalConfig(prev => ({
    //         ...prev,
    //         visible: false,
    //         onConfirm: null,
    //         onCancel: null
    //     }));
    // };

    // Supsis kullanımı durdurulmuştur
    // const handleSubcompanyChangeConfirm = () => {
    //     const {onConfirm} = subcompanyChangeModalConfig;
    //     closeSubcompanyChangeModal();
    //     if (typeof onConfirm === "function") {
    //         onConfirm();
    //     }
    // };

    // Supsis kullanımı durdurulmuştur
    // const handleSubcompanyChangeCancel = () => {
    //     const {onCancel} = subcompanyChangeModalConfig;
    //     closeSubcompanyChangeModal();
    //     if (typeof onCancel === "function") {
    //         onCancel();
    //     }
    // };

    // Supsis kullanımı durdurulmuştur
    // const handleSupsisButtonClick = e => {
    //     if (e && e.isTrusted === false) { return true; }
    //     if (isOpeningSupsis) { return false; }
    //     if (e) { e.preventDefault(); e.stopPropagation(); }
    //     if (subcompanyChangeModalConfig.visible) { return false; }
    //     if (!isSupsisActive) {
    //         if (window.__supsisModalOpen) return false;
    //         setIsOpeningSupsis(true);
    //         openSupsisConfirmationModal({
    //             subcompanyName: getSubcompanyName(stores.meStore.selectedSubCompanyId),
    //             onConfirm: () => { initSupsisWithCurrentSubcompany(); },
    //             onCancel: () => { setIsOpeningSupsis(false); }
    //         });
    //         return false;
    //     }
    //     if (!supsisActiveSubcompanyId) return false;
    //     if (supsisActiveSubcompanyId !== stores.meStore.selectedSubCompanyId) {
    //         openSubcompanyChangeModal({
    //             currentName: getSubcompanyName(supsisActiveSubcompanyId),
    //             newName: getSubcompanyName(stores.meStore.selectedSubCompanyId),
    //             onConfirm: () => {
    //                 const currentSubcompanyId = stores.meStore.selectedSubCompanyId;
    //                 setSupsisActiveSubcompanyId(currentSubcompanyId);
    //                 restartSupsisChat();
    //             },
    //             onCancel: () => { stores.meStore.setSelectedSubCompanyId(supsisActiveSubcompanyId); }
    //         });
    //         return false;
    //     }
    //     return true;
    // };

    // Supsis kullanımı durdurulmuştur
    // const initSupsisWithCurrentSubcompany = () => {
    //     if (isOpeningSupsis) { return; }
    //     window.__supsisModalOpen = false;
    //     setIsOpeningSupsis(true);
    //     const currentSubcompanyId = stores.meStore.selectedSubCompanyId;
    //     setSupsisActiveSubcompanyId(currentSubcompanyId);
    //     setIsSupsisActive(true);
    //     if (!window.supsis) { setIsOpeningSupsis(false); return; }
    //     updateSupsisUserData();
    //     const openSupsisWidget = () => {
    //         const methods = [
    //             () => window.supsis("widget:visibility", true),
    //             () => typeof window.supsis.show === "function" && window.supsis.show(),
    //             () => typeof window.supsis.open === "function" && window.supsis.open(),
    //             () => typeof window.supsis === "function" && window.supsis("open")
    //         ];
    //         methods.forEach(method => { try { method(); } catch (e) {} });
    //     };
    //     setTimeout(() => {
    //         openSupsisWidget();
    //         setTimeout(() => {
    //             const containers = document.querySelectorAll('[class*="supsis"], [id*="supsis"]');
    //             containers.forEach(container => {
    //                 if (container.style.display === "none") { container.style.display = "block"; }
    //             });
    //             const widgetButton = document.querySelector("#ls-openButton");
    //             if (widgetButton) { widgetButton.click(); }
    //             setIsOpeningSupsis(false);
    //         }, 2000);
    //     }, 500);
    // };

    // Supsis kullanımı durdurulmuştur
    // const updateSupsisUserData = () => {
    //     if (!window.supsis) return;
    //     const companyName = stores?.meStore?.companies?.[0]?.companyName;
    //     const subCompanyId = stores?.meStore?.selectedSubCompanyId;
    //     const subCompanyName = stores?.meStore?.subcompanies?.find(s => s._id === subCompanyId)?.name;
    //     const city = stores?.meStore?.subcompanies?.find(s => s._id === subCompanyId)?.city;
    //     const fullName = stores?.meStore?.fullName || "";
    //     const companyId = stores?.meStore?.companies?.[0]?._id;
    //     const companyType = /* ... */;
    //     const userData = {
    //         fullname: `${subCompanyName} / ${fullName}`,
    //         email: `${config?.isDev ? "https://app-dev.vigo.ist/" : "https://app.vigo.ist/"}companies/${companyId}`,
    //         phone: stores?.meStore?.phone || "",
    //         company_type: companyType,
    //         company_name: companyName,
    //         subcompany_name: subCompanyName,
    //         subcompany_city: city
    //     };
    //     window.supsis("ready", supsis => {
    //         supsis.setUserData(userData);
    //         supsis.setContactProperty({ company_type: companyType, company_name: companyName, subcompany_name: subCompanyName, subcompany_city: city });
    //     });
    // };

    // Supsis kullanımı durdurulmuştur
    // const initSupsis = () => {
    //     if (!isCompanyUser || !isLoggedIn) return;
    //     let script1 = document.createElement("script");
    //     script1.innerHTML = `window.supsis = window.supsis || function () { (supsis.q = supsis.q || []).push(arguments) }; supsis.l = +new Date;`;
    //     document.body.appendChild(script1);
    //     let script2 = document.createElement("script");
    //     script2.src = "https://vigo.visitor.supsis.live/static/js/loader.js";
    //     script2.async = true;
    //     script2.defer = true;
    //     script2.onload = () => {
    //         setTimeout(() => {
    //             if (window.supsis) {
    //                 updateSupsisUserData();
    //                 window.supsis("widget:visibility", false);
    //                 window.supsis("ready", supsis => {
    //                     if (supsis.on) {
    //                         supsis.on("minimize", () => { setIsSupsisActive(false); setIsOpeningSupsis(false); });
    //                         supsis.on("close", () => { setIsSupsisActive(false); setIsOpeningSupsis(false); setSupsisActiveSubcompanyId(null); });
    //                         supsis.on("maximize", () => { setIsSupsisActive(true); });
    //                     }
    //                 });
    //                 return;
    //             }
    //         }, 100);
    //     };
    //     document.body.appendChild(script2);
    // };

    // Supsis kullanımı durdurulmuştur
    // const draggableSupsis = () => {
    //     const dragElement = elmnt => { /* ... drag logic ... */ };
    //     const interval = setInterval(() => {
    //         const button = document.getElementById("ls-openButton");
    //         if (button) {
    //             button.style.position = "fixed";
    //             if (!window.__supsisClickSetupDone) {
    //                 button.removeEventListener("click", handleSupsisButtonClick, true);
    //                 button.addEventListener("click", handleSupsisButtonClick, true);
    //                 window.__supsisClickSetupDone = true;
    //             }
    //             dragElement(button);
    //             clearInterval(interval);
    //         }
    //     }, 1000);
    // };

    // Supsis kullanımı durdurulmuştur
    // useEffect(() => {
    //     const cleanup = () => {
    //         if (window.supsis) {
    //             try { window.supsis("widget:visibility", false); window.supsis("destroy"); } catch (e) { console.error("Supsis cleanup error:", e); }
    //         }
    //         document.querySelectorAll('script[src*="supsis"]').forEach(s => s.parentNode?.removeChild(s));
    //         document.querySelector(".supsis-widget-container")?.remove();
    //         delete window.supsis;
    //         delete window.__supsisClickSetupDone;
    //         delete window.__supsisModalOpen;
    //     };
    //     if (isLoggedIn && isCompanyUser) {
    //         initSupsis();
    //         draggableSupsis();
    //     } else {
    //         cleanup();
    //         setIsSupsisActive(false);
    //         setSupsisActiveSubcompanyId(null);
    //     }
    //     return cleanup;
    // }, [isLoggedIn, isCompanyUser]);

    // Supsis kullanımı durdurulmuştur
    // useEffect(() => {
    //     if (subcompanyChangeModalConfig.visible) return;
    //     if (supsisConfirmationModalConfig.visible) return;
    //     if (isOpeningSupsis) return;
    //     if (!isSupsisActive) return;
    //     if (!supsisActiveSubcompanyId) return;
    //     if (!stores.meStore.selectedSubCompanyId) return;
    //     const currentSelectedId = stores.meStore.selectedSubCompanyId;
    //     if (supsisActiveSubcompanyId !== currentSelectedId) {
    //         openSubcompanyChangeModal({
    //             currentName: getSubcompanyName(supsisActiveSubcompanyId),
    //             newName: getSubcompanyName(currentSelectedId),
    //             onConfirm: () => { setSupsisActiveSubcompanyId(currentSelectedId); restartSupsisChat(); },
    //             onCancel: () => { stores.meStore.setSelectedSubCompanyId(supsisActiveSubcompanyId); }
    //         });
    //     }
    // }, [stores.meStore.selectedSubCompanyId, isSupsisActive, supsisActiveSubcompanyId, subcompanyChangeModalConfig.visible, supsisConfirmationModalConfig.visible, isOpeningSupsis]);

    return useObserver(() => (
        <>
            <MenuPopupGlobalStyle />
            {!isLoggedIn || window.location.pathname.indexOf("password-reset") !== -1 ? (
                <LoginLayout>{props.children}</LoginLayout>
            ) : role === "cUser" ? (
                <CustomerTheme content={props.children} />
            ) : (
                <LayoutContainer>
                    <Loading />
                    {isMobile && (
                        <MobileMenuToggle
                            pathname={history.location.pathname}
                            onClick={() => setCollapsed(!collapsed)}
                            collapsed={collapsed}
                        >
                            {!collapsed ? "" : <MenuOutlined style={{color: "black"}} />}
                        </MobileMenuToggle>
                    )}

                    {isLoggedIn && !stores.appStore.loading && (
                        <LayoutSider
                            width={260}
                            collapsedWidth={isMobile ? 60 : 80}
                            collapsible
                            collapsed={collapsed}
                            onCollapse={setCollapsed}
                            pathname={history.location.pathname}
                        >
                            <LogoContainer onClick={() => setCollapsed(!collapsed)}>
                                {!isMobile && <VigoMobileLogo />}
                                {(collapsed || (isMobile && !collapsed)) && (
                                    <LogoImg
                                        src={V_LOGO}
                                        style={{
                                            height: isMobile ? "60px" : "40px",
                                            width: isMobile ? "60px" : "40px",
                                            backgroundColor: "#000",
                                            display: isMobile ? "flex" : undefined,
                                            margin: "20px auto"
                                        }}
                                    />
                                )}
                                {!collapsed && (
                                    <LogoImg
                                        src={VIGO_PANEL_LOGO}
                                        style={{
                                            height: "60px",
                                            marginTop: "8px"
                                        }}
                                    />
                                )}
                            </LogoContainer>

                            {!collapsed && (
                                <MenuSearch value={menuSearchValue} onChange={setMenuSearchValue} />
                            )}

                            <Menu
                                theme="dark"
                                selectedKeys={[pathname]}
                                mode="inline"
                                openKeys={menuOpenKeys}
                                onOpenChange={keys => {
                                    if (!menuSearchValue?.trim()) {
                                        setMenuOpenKeys(keys);
                                    }
                                }}
                                onClick={({key, domEvent}) => {
                                    domEvent.preventDefault();
                                    history.push(key);
                                    if (isMobile && !collapsed) {
                                        setCollapsed(true);
                                    }
                                }}
                            >
                                {menuSearchValue?.trim() && !hasSearchResults() && (
                                    <Menu.Item
                                        key="no-results"
                                        disabled
                                        style={{
                                            color: "rgba(255, 255, 255, 0.5)",
                                            textAlign: "center",
                                            cursor: "default"
                                        }}
                                    >
                                        Sonuç bulunamadı
                                    </Menu.Item>
                                )}
                                {getRoutes()
                                    .filter(
                                        route =>
                                            route.navPosition === "top" &&
                                            route.roles.includes(role) &&
                                            (matchesSearch(route) || hasMatchingSubMenu(route))
                                    )
                                    .map((route, i, arr) => {
                                        // Rol filtresinden sonra görünür alt menü tek kaldıysa
                                        // (ve tıklanamaz bir başlık değilse) üst menü açılır alt
                                        // menü yerine o alt menüye giden tekil link gibi davranır.
                                        const visibleSubMenus = route.subMenus?.filter(subRoute =>
                                            subRoute.roles.includes(role)
                                        );
                                        const singleSubMenu =
                                            visibleSubMenus?.length === 1 &&
                                            !visibleSubMenus[0].isHeader
                                                ? visibleSubMenus[0]
                                                : null;
                                        const singleSubMenuPath = singleSubMenu
                                            ? singleSubMenu.path ||
                                              `${route.path || ""}${singleSubMenu.subPath || ""}`
                                            : null;
                                        const resolvedPath = singleSubMenu
                                            ? singleSubMenuPath
                                            : route.companyUserPage && route.path === "/companies"
                                            ? "/companies/" + stores?.meStore?.company?._id
                                            : route.path;

                                        return (
                                        <React.Fragment key={i}>
                                            {(!route.subMenus || singleSubMenu) && (
                                                <Menu.Item
                                                    style={{
                                                        marginBottom:
                                                            arr.length - 1 === i && arr.length > 1
                                                                ? "80px"
                                                                : "0px",
                                                        fontWeight: route?.bold ? "bold" : "normal"
                                                    }}
                                                    data-testid={route["data-testid"]}
                                                    key={resolvedPath}
                                                    disabled={
                                                        !singleSubMenu &&
                                                        route.companyUserPage &&
                                                        !stores?.meStore?.company?._id
                                                    }
                                                >
                                                    <Link to={resolvedPath}>
                                                        {route.icon}
                                                        <MenuText>
                                                            {singleSubMenu && route.collapsedName
                                                                ? route.collapsedName()
                                                                : route.name()}
                                                        </MenuText>
                                                    </Link>
                                                </Menu.Item>
                                            )}
                                            {route.subMenus && !singleSubMenu && (
                                                <SubMenu
                                                    key={
                                                        route.companyUserPage &&
                                                        route.path === "/companies"
                                                            ? "/companies/" +
                                                              stores?.meStore?.company?._id
                                                            : route.path
                                                    }
                                                    icon={route.icon}
                                                    title={
                                                        <React.Fragment>
                                                            <MenuText>{route.name()}</MenuText>
                                                            {route.suffixIcon &&
                                                                canSeeSuffixIcon(route) && (
                                                                    <MenuSuffixIcon
                                                                        icon={route.suffixIcon}
                                                                    />
                                                                )}
                                                        </React.Fragment>
                                                    }
                                                    popupOffset={[2, 0]}
                                                >
                                                    {visibleSubMenus.map(subRoute => {
                                                        const subRoutePath =
                                                            subRoute.path ||
                                                            `${route.path ||
                                                                ""}${subRoute.subPath || ""}`;
                                                        const hasMenuExtras = !!(
                                                            subRoute?.suffixIcon || subRoute?.badge
                                                        );
                                                        const menuTextStyle = {
                                                            ...(subRoute?.isSmallFont
                                                                ? {fontSize: "13px"}
                                                                : {}),
                                                            ...(hasMenuExtras
                                                                ? menuExtrasTextStyle
                                                                : {})
                                                        };

                                                        return (
                                                            <Menu.Item
                                                                className={`${
                                                                    isMenuActive(subRoutePath)
                                                                        ? "ant-menu-item-selected"
                                                                        : ""
                                                                } ${
                                                                    subRoute.isSmallFont
                                                                        ? "small-font-item"
                                                                        : ""
                                                                }`}
                                                                data-testid={
                                                                    subRoute["data-testid"]
                                                                }
                                                                key={subRoutePath}
                                                                disabled={
                                                                    subRoute?.isHeader ||
                                                                    (subRoute.companyUserPage &&
                                                                        !stores?.meStore?.company
                                                                            ?._id)
                                                                }
                                                                style={
                                                                    (subRoute.isSmallFont
                                                                        ? {fontSize: "12px"}
                                                                        : undefined,
                                                                    subRoute?.bold
                                                                        ? {fontWeight: "bold"}
                                                                        : undefined)
                                                                }
                                                            >
                                                                {subRoute?.isHeader ? (
                                                                    <span
                                                                        style={
                                                                            hasMenuExtras
                                                                                ? menuExtrasRowStyle
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {subRoute?.icon}
                                                                        <MenuText
                                                                            style={menuTextStyle}
                                                                        >
                                                                            {subRoute?.name()}
                                                                        </MenuText>
                                                                        {subRoute?.suffixIcon &&
                                                                            canSeeSuffixIcon(
                                                                                subRoute
                                                                            ) && (
                                                                                <MenuSuffixIcon
                                                                                    icon={
                                                                                        subRoute.suffixIcon
                                                                                    }
                                                                                />
                                                                            )}
                                                                        {subRoute?.badge && (
                                                                            <MenuBadge
                                                                                label={
                                                                                    subRoute.badge
                                                                                }
                                                                            />
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <Link
                                                                        to={subRoutePath}
                                                                        style={
                                                                            hasMenuExtras
                                                                                ? menuExtrasRowStyle
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {subRoute?.icon}
                                                                        {subRoute?.isSmallFont && (
                                                                            <span
                                                                                style={{
                                                                                    display:
                                                                                        "inline-block",
                                                                                    width: "6px",
                                                                                    height: "6px",
                                                                                    backgroundColor:
                                                                                        "#888",
                                                                                    borderRadius:
                                                                                        "50%",
                                                                                    marginRight:
                                                                                        "8px",
                                                                                    verticalAlign:
                                                                                        "middle"
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <MenuText
                                                                            style={menuTextStyle}
                                                                        >
                                                                            {subRoute?.name()}
                                                                        </MenuText>
                                                                        {subRoute?.suffixIcon &&
                                                                            canSeeSuffixIcon(
                                                                                subRoute
                                                                            ) && (
                                                                                <MenuSuffixIcon
                                                                                    icon={
                                                                                        subRoute.suffixIcon
                                                                                    }
                                                                                />
                                                                            )}
                                                                        {subRoute?.badge && (
                                                                            <MenuBadge
                                                                                label={
                                                                                    subRoute.badge
                                                                                }
                                                                            />
                                                                        )}
                                                                    </Link>
                                                                )}
                                                            </Menu.Item>
                                                        );
                                                    })}
                                                </SubMenu>
                                            )}
                                        </React.Fragment>
                                        );
                                    })}
                            </Menu>
                            <BottomMenu
                                theme="dark"
                                selectedKeys={[
                                    stores.appStore.currentPath &&
                                        stores.appStore.currentPath.selectedPath
                                ]}
                                mode="inline"
                                onClick={({key, domEvent}) => {
                                    domEvent.preventDefault();
                                    if (key === "logout") {
                                        stores.meStore.logout();
                                        history.push("/login");
                                    } else {
                                        history.push(key);
                                    }
                                }}
                            >
                                {getRoutes()
                                    .filter(
                                        route =>
                                            route.navPosition === "bottom" &&
                                            route.roles.includes(role)
                                    )
                                    .map(route => (
                                        <Menu.Item key={route.path}>
                                            <Link to={route.path}>
                                                {route.icon}
                                                <MenuText>{route.name()}</MenuText>
                                            </Link>
                                        </Menu.Item>
                                    ))}
                            </BottomMenu>
                        </LayoutSider>
                    )}
                    <RightLayout
                        isloggedin={isLoggedIn ? 1 : undefined}
                        collapsed={collapsed ? 1 : undefined}
                        pathname={history.location.pathname}
                    >
                        <div data-testid="mainLayoutHeader">
                            {isLoggedIn && (
                                <LayoutHeader
                                    title={
                                        isMobile && isMap(history.location.pathname)
                                            ? ""
                                            : role === "cUser"
                                            ? companies.length > 1
                                                ? "Vigo Panel"
                                                : `${companies[0].companyName} / Yönetim Paneli`
                                            : company
                                            ? `${company.companyName} / Yönetim Paneli`
                                            : "Vigo Panel"
                                    }
                                    extra={extra}
                                ></LayoutHeader>
                            )}
                        </div>
                        <LayoutContent pathname={pathname} isloggedin={isLoggedIn ? 1 : undefined}>
                            <ChildrenContainer pathname={pathname} className={"children-container"}>
                                {props.children}
                            </ChildrenContainer>
                        </LayoutContent>
                        <LayoutFooter>Vigo Panel {moment().year() || 2021}</LayoutFooter>
                    </RightLayout>
                </LayoutContainer>
            )}
            {/* Supsis kullanımı durdurulmuştur */}
            {/* <SupsisConfirmationModal
                visible={supsisConfirmationModalConfig.visible}
                subcompanyName={supsisConfirmationModalConfig.subcompanyName}
                onConfirm={handleSupsisConfirmationOk}
                onCancel={handleSupsisConfirmationCancel}
            /> */}
            {/* Supsis kullanımı durdurulmuştur */}
            {/* <SupsisSubcompanyChangeModal
                visible={subcompanyChangeModalConfig.visible}
                currentName={subcompanyChangeModalConfig.currentName}
                newName={subcompanyChangeModalConfig.newName}
                okText={subcompanyChangeModalConfig.okText}
                cancelText={subcompanyChangeModalConfig.cancelText}
                onConfirm={handleSubcompanyChangeConfirm}
                onCancel={handleSubcompanyChangeCancel}
            /> */}
        </>
    ));
});
